import {
  generateOccurrences,
  plainDateFromIso,
  schemaGenerable,
  typeDuSchema,
  type CustodyOccurrence,
  type ParentSide,
  type SchemaDeGarde,
} from '@repere/core';
import { Invisible } from '../ui';
import { cleDuJour, dateComplete, grilleDuMois, moisEtAnnee, nomsJoursCourts } from './dates';
import { useTraduction } from '../i18n/langue';

/** Trois mois : un cycle de quinzaine s'y répète six fois, assez pour qu'on voie le motif. */
const MOIS_AFFICHES = 3;

/**
 * Aperçu de l'alternance, à la création du dossier.
 *
 * Un schéma de garde se choisit dans une liste déroulante, et « 2-2-5-5 » ne dit rien à
 * qui ne l'a jamais pratiqué — pas plus que « 3-4-4-3 », dont la phrase d'explication est
 * presque la même. Le parent choisissait donc à l'aveugle le rythme de vie de ses enfants,
 * et ne le découvrait qu'une fois le dossier créé, sur un calendrier qu'il faut ensuite
 * faire modifier avec l'accord de l'autre parent.
 *
 * Trois décisions :
 *
 * 1. **Les périodes viennent du noyau**, par la même fonction que l'API. Un aperçu qui
 *    recalculerait l'alternance de son côté finirait par montrer autre chose que le
 *    calendrier du dossier — et c'est précisément sur cet écran qu'un écart se paierait.
 * 2. **Un jour appartient à qui a l'enfant la nuit qui commence**, exactement comme dans
 *    `Calendrier` : la période court de son instant de début à son instant de fin, et
 *    chaque journée entamée est marquée. Les deux vues sont ainsi d'accord par
 *    construction, y compris sur les passages de bras de milieu de journée.
 * 3. **La couleur ne porte pas seule l'information** (WCAG 1.4.1). Les deux fonds se
 *    séparent d'abord par la clarté, le passage de bras porte un trait, la phrase de tête
 *    compte les jours de chacun, et chaque case est nommée pour les lecteurs d'écran.
 */
export function ApercuAlternance({
  schema,
  debut,
  heure,
  quiCommence,
}: {
  schema: SchemaDeGarde;
  /** `AAAA-MM-JJ`, ou chaîne vide tant que la date n'est pas saisie. */
  debut: string;
  /** `HH:MM`. */
  heure: string;
  /** Rang du parent qui ouvre le cycle. 1 est toujours celui qui crée le dossier. */
  quiCommence: '1' | '2';
}) {
  const { locale, messages } = useTraduction();
  const m = messages.apercu;
  const description = messages.schemasDeGarde.schemas[schema];

  if (!schemaGenerable(schema)) {
    return (
      <section className="apercu-alternance" aria-labelledby="apercu-titre">
        <h3 id="apercu-titre">{m.titre}</h3>
        {/* Ni alerte ni avertissement : l'absence d'aperçu n'est pas un manque du produit
            ni une erreur du parent, c'est ce que ce schéma veut dire. */}
        <p className="discret" style={{ margin: 0 }}>{m.pasDeCycle(description.libelle)}</p>
      </section>
    );
  }

  if (!debut) {
    return (
      <section className="apercu-alternance" aria-labelledby="apercu-titre">
        <h3 id="apercu-titre">{m.titre}</h3>
        {/* Pas de calendrier fantôme : réserver trois cents pixels de gris pour annoncer
            un calendrier ressemble à un chargement en cours, et dit moins bien la même
            chose qu'une phrase. Le bloc reste court tant qu'il n'a rien à montrer. */}
        <p className="discret" style={{ margin: 0 }}>{m.choisirDate}</p>
      </section>
    );
  }

  const depart = plainDateFromIso(debut);
  const [heureH = '16', heureM = '30'] = heure.split(':');
  const premierMois = new Date(depart.year, depart.month - 1, 1);
  const finFenetre = new Date(depart.year, depart.month - 1 + MOIS_AFFICHES, 0);

  const periodes = generateOccurrences(
    {
      type: typeDuSchema(schema),
      startsOn: depart,
      handoverTime: { hour: Number(heureH), minute: Number(heureM) },
      startingParent: quiCommence === '1' ? 'A' : 'B',
    },
    {
      from: { year: premierMois.getFullYear(), month: premierMois.getMonth() + 1, day: 1 },
      to: {
        year: finFenetre.getFullYear(),
        month: finFenetre.getMonth() + 1,
        day: finFenetre.getDate(),
      },
    },
  );

  const parJour = cotesParJour(periodes);
  const mois = Array.from({ length: MOIS_AFFICHES }, (_, n) => {
    const quand = new Date(depart.year, depart.month - 1 + n, 1);
    return { quand, semaines: grilleDuMois(quand.getFullYear(), quand.getMonth()) };
  });

  // Le compte ne porte que sur les jours affichés : « 46 jours chez vous » suivi d'un
  // calendrier où l'on n'en compterait pas autant serait pire que pas de compte du tout.
  let chezMoi = 0;
  let chezAutre = 0;
  for (const { semaines } of mois) {
    for (const semaine of semaines) {
      for (const jour of semaine) {
        if (!jour) continue;
        const cote = parJour.get(cleDuJour(jour));
        if (cote === 'A') chezMoi += 1;
        else if (cote === 'B') chezAutre += 1;
      }
    }
  }

  const joursCourts = nomsJoursCourts(locale);

  return (
    <section className="apercu-alternance" aria-labelledby="apercu-titre">
      <h3 id="apercu-titre">{m.titre}</h3>
      <p className="discret apercu-alternance-resume">
        {m.aPartirDu(dateComplete(new Date(depart.year, depart.month - 1, depart.day), locale))}{' '}
        <strong>{m.joursChezVous(chezMoi)}</strong> {m.etJoursChezAutre(chezAutre)}
      </p>

      <div className="apercu-alternance-mois">
        {mois.map(({ quand, semaines }) => (
          <table className="calendrier compact" key={quand.getTime()}>
            <caption>{moisEtAnnee(quand, locale)}</caption>
            <thead>
              <tr>
                {joursCourts.map((jour) => (
                  <th scope="col" key={jour}>
                    {jour}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {semaines.map((semaine, rang) => (
                <tr key={rang}>
                  {semaine.map((jour, colonne) => {
                    if (!jour) return <td key={colonne} />;
                    const cote = parJour.get(cleDuJour(jour));
                    const veille = new Date(
                      jour.getFullYear(),
                      jour.getMonth(),
                      jour.getDate() - 1,
                    );
                    const passage = cote !== undefined && parJour.get(cleDuJour(veille)) !== cote;
                    return (
                      <td
                        key={colonne}
                        className={classeDuJour(cote, passage)}
                      >
                        {jour.getDate()}
                        {cote ? (
                          <Invisible>
                            {estChezMoi(cote) ? m.chezVousInvisible : m.chezAutreInvisible}
                          </Invisible>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        ))}
      </div>

      <ul className="legende">
        <li>
          <span className="temoin-garde chez-moi" aria-hidden="true" /> {m.legendeChezVous}
        </li>
        <li>
          <span className="temoin-garde chez-autre" aria-hidden="true" /> {m.legendeChezAutre}
        </li>
        <li>
          <span className="temoin-garde passage" aria-hidden="true" /> {m.legendePassage}
        </li>
      </ul>
    </section>
  );
}

/**
 * Un jour appartient au parent dont la période l'a entamé.
 *
 * C'est la règle de `Calendrier`, reprise mot pour mot : la période court d'un instant à
 * un autre — 16h30 le vendredi jusqu'à 16h30 le dimanche — et chaque journée commencée
 * est marquée. Les deux écrans doivent répondre pareil, sans quoi l'aperçu promettrait un
 * calendrier différent de celui que le dossier affichera.
 */
function cotesParJour(periodes: CustodyOccurrence[]): Map<string, ParentSide> {
  const parJour = new Map<string, ParentSide>();
  for (const periode of periodes) {
    for (
      const jour = new Date(periode.start);
      jour < periode.end;
      jour.setDate(jour.getDate() + 1)
    ) {
      parJour.set(cleDuJour(jour), periode.parent);
    }
  }
  return parJour;
}

/**
 * Le côté `A` est le parent 1, c'est-à-dire toujours celui qui crée le dossier.
 *
 * Qui *commence* n'entre pas ici : `startingParent` le porte déjà, et les périodes
 * rendues nomment le parent, pas son rang dans le cycle.
 */
const estChezMoi = (cote: ParentSide): boolean => cote === 'A';

function classeDuJour(cote: ParentSide | undefined, passage: boolean): string | undefined {
  if (!cote) return undefined;
  const garde = estChezMoi(cote) ? 'chez-moi' : 'chez-autre';
  return passage ? `${garde} passage` : garde;
}
